import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './Travaux.css';

// Import des données
import projectsData from '../../../data/projets.json';

// Configuration des technologies avec leurs couleurs
const techTags = [
  { id: 'all', name: 'Tous', color: '#b387f6', icon: 'fas fa-asterisk' },
  { id: 'html', name: 'HTML', color: '#e34c26', icon: 'fas fa-code' },
  { id: 'css', name: 'CSS', color: '#264de4', icon: 'fas fa-palette' },
  { id: 'js', name: 'JavaScript', color: '#f7df1e', icon: 'fas fa-brackets-curly' },
  { id: 'react', name: 'React', color: '#61dafb', icon: 'fab fa-react' },
  { id: 'php', name: 'PHP', color: '#777bb3', icon: 'fab fa-php' },
  { id: 'mysql', name: 'MySQL', color: '#4479a1', icon: 'fas fa-database' },
  { id: 'symfony', name: 'Symfony', color: '#000000', icon: 'fab fa-symfony' },
  { id: 'api', name: 'API', color: '#4CAF50', icon: 'fas fa-exchange-alt' },
  { id: 'AI', name: 'AI', color: '#0db7ed', icon: 'fa-solid fa-robot' },
  { id: 'nosql', name: 'NoSQL', color: '#4DB33D', icon: 'fas fa-leaf' },
  { id: 'sass', name: 'SASS', color: '#CC6699', icon: 'fab fa-sass' }
];

const Travaux = ({ isVisible, onClose }) => {
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [isFiltering, setIsFiltering] = useState(false);
  const [dateFilter, setDateFilter] = useState('all');
  const [animatedItems, setAnimatedItems] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isLoaded, setIsLoaded] = useState(false);
  
  const containerRef = useRef(null);
  const particlesRef = useRef(null);
  const skillsSphereRef = useRef(null);
  
  // Variables pour les particules
  const numParticles = 60;
  const particles = useRef([]);
  
  // Initialisation des particules
  useEffect(() => {
    if (isVisible && particlesRef.current) {
      for (let i = 0; i < numParticles; i++) {
        particles.current.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          size: Math.random() * 3 + 1,
          speedX: Math.random() * 2 - 1,
          speedY: Math.random() * 2 - 1,
          opacity: Math.random() * 0.5 + 0.1,
        });
      }
      
      const canvas = particlesRef.current;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      animateParticles();
    }
    
    // Nettoyage
    return () => {
      particles.current = [];
    };
  }, [isVisible]);
  
  // Animation des particules
  const animateParticles = () => {
    if (!particlesRef.current || !isVisible) return;
    
    const canvas = particlesRef.current;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    particles.current.forEach(particle => {
      // Mouvement
      particle.x += particle.speedX;
      particle.y += particle.speedY;
      
      // Rebond sur les bords
      if (particle.x < 0 || particle.x > canvas.width) particle.speedX *= -1;
      if (particle.y < 0 || particle.y > canvas.height) particle.speedY *= -1;
      
      // Dessin de la particule
      ctx.beginPath();
      ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(179, 135, 246, ${particle.opacity})`;
      ctx.fill();
      
      // Connexion entre particules proches
      particles.current.forEach(otherParticle => {
        const dx = particle.x - otherParticle.x;
        const dy = particle.y - otherParticle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        if (distance < 100) {
          ctx.beginPath();
          ctx.moveTo(particle.x, particle.y);
          ctx.lineTo(otherParticle.x, otherParticle.y);
          ctx.strokeStyle = `rgba(179, 135, 246, ${0.2 - distance/500})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
        }
      });
    });
    
    requestAnimationFrame(animateParticles);
  };
  
  // Effet de suivi du curseur
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      
      setMousePosition({ x, y });
    };
    
    if (isVisible) {
      window.addEventListener('mousemove', handleMouseMove);
      // Animation d'entrée
      setTimeout(() => setIsLoaded(true), 300);
      
      // Animation séquentielle des éléments
      const items = ['header', 'sphere', 'metrics', 'controls', 'projects'];
      let timer = 0;
      
      const animationInterval = setInterval(() => {
        if (timer < items.length) {
          setAnimatedItems(prev => [...prev, items[timer]]);
          timer++;
        } else {
          clearInterval(animationInterval);
        }
      }, 200);
      
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        clearInterval(animationInterval);
        setAnimatedItems([]);
      };
    } else {
      setIsLoaded(false);
    }
  }, [isVisible]);
  
  // Préparation des données de projets
  useEffect(() => {
    const formattedProjects = [];
    
    Object.keys(projectsData).forEach(category => {
      if (projectsData[category].projects) {
        projectsData[category].projects.forEach((project, index) => {
          // Quelques projets sont marqués comme récents ou en vedette pour l'exemple
          const isRecent = index === 0; // Premier projet de chaque catégorie
          const isFeatured = index === 1; // Deuxième projet de chaque catégorie
          
          // Ajouter des dates fictives pour la démo
          // Dans un environnement réel, ces dates viendraient de vos données
          const currentDate = new Date();
          let projectDate;
          
          if (index === 0) {
            // Projets très récents (dernier mois)
            projectDate = new Date(currentDate);
            projectDate.setMonth(currentDate.getMonth() - 1);
          } else if (index === 1) {
            // Projets récents (3-6 mois)
            projectDate = new Date(currentDate);
            projectDate.setMonth(currentDate.getMonth() - Math.floor(Math.random() * 3 + 3));
          } else {
            // Projets plus anciens (6-18 mois)
            projectDate = new Date(currentDate);
            projectDate.setMonth(currentDate.getMonth() - Math.floor(Math.random() * 12 + 6));
          }
          
          formattedProjects.push({
            id: `${category}-${index}`,
            title: project.title || `Projet ${category} ${index + 1}`,
            category,
            categoryName: category.charAt(0).toUpperCase() + category.slice(1),
            description: project.description || projectsData[category].text,
            image: project.couverture,
            link: project.link,
            tags: project.tags || [],
            isRecent,
            isFeatured,
            date: projectDate // Ajout de la date du projet
          });
        });
      }
    });
    
    setProjects(formattedProjects);
    setFilteredProjects(formattedProjects);
    
    // Simuler un chargement pour l'animation d'entrée
    setTimeout(() => {
      setLoaded(true);
    }, 300);
  }, []);
  
  // Filtrage des projets
  useEffect(() => {
    // Effet visuel lors du changement de filtre
    setIsFiltering(true);
    setTimeout(() => {
      setIsFiltering(false);
    }, 600);
    
    // Filtrage des projets
    let result = [...projects];
    
    // Filtre par tag
    if (activeFilter !== 'all') {
      result = result.filter(project => project.tags.includes(activeFilter));
    }
    
    // Filtre par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        project => 
          project.title.toLowerCase().includes(query) || 
          project.category.toLowerCase().includes(query) ||
          project.tags.some(tag => tag.toLowerCase().includes(query))
      );
    }
    
    // Filtre par date
    if (dateFilter !== 'all') {
      const currentDate = new Date();
      let filterDate;
      
      switch (dateFilter) {
        case 'last-month':
          // Projets du dernier mois
          filterDate = new Date(currentDate);
          filterDate.setMonth(currentDate.getMonth() - 1);
          result = result.filter(project => project.date >= filterDate);
          break;
          
        case 'last-six-months':
          // Projets des 6 derniers mois
          filterDate = new Date(currentDate);
          filterDate.setMonth(currentDate.getMonth() - 6);
          result = result.filter(project => project.date >= filterDate);
          break;
          
        case 'last-year':
          // Projets de l'année dernière
          filterDate = new Date(currentDate);
          filterDate.setFullYear(currentDate.getFullYear() - 1);
          result = result.filter(project => project.date >= filterDate);
          break;
          
        case 'older':
          // Projets de plus d'un an
          filterDate = new Date(currentDate);
          filterDate.setFullYear(currentDate.getFullYear() - 1);
          result = result.filter(project => project.date < filterDate);
          break;
          
        default:
          // Pas de filtrage par date
          break;
      }
    }
    
    setFilteredProjects(result);
  }, [activeFilter, searchQuery, dateFilter, projects]);
  
  // Gestion des projets sélectionnés
  const handleProjectClick = (project) => {
    setSelectedProject(project);
  };
  
  // Fermeture du modal
  const closeProjectDetails = () => {
    setSelectedProject(null);
  };
  
  // Formater la date pour l'affichage
  const formatDate = (date) => {
    if (!date) return '';
    
    const options = { year: 'numeric', month: 'short' };
    return new Date(date).toLocaleDateString('fr-FR', options);
  };
  
  // Définition de la position du gradient qui suit la souris
  const gradientPosition = `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(179, 135, 246, 0.2) 0%, rgba(92, 122, 250, 0.1) 40%, transparent 70%)`;
  
  // Liste des compétences pour la sphère 3D
  const skills = [
    { name: 'HTML', color: '#e34c26' },
    { name: 'CSS', color: '#264de4' },
    { name: 'JavaScript', color: '#f7df1e' },
    { name: 'React', color: '#61dafb' },
    { name: 'PHP', color: '#777bb3' },
    { name: 'MySQL', color: '#4479a1' },
    { name: 'Git', color: '#f05032' },
    { name: 'Bootstrap', color: '#7952b3' },
    { name: 'Symfony', color: '#000000' },
    { name: 'SASS', color: '#cc6699' },
    { name: 'Angular', color: '#8dd6f9' },
    { name: 'APIs', color: '#4CAF50' },
    { name: 'UI/UX', color: '#ff7f50' },
    { name: 'Responsive', color: '#3498db' },
    { name: 'MongoDB', color: '#ffca28' },
    { name: 'Docker', color: '#0db7ed' },
    { name: 'NoSQL', color: '#4DB33D' }
  ];
  
  return (
    <div 
      className={`travaux-section ${isVisible ? 'visible' : ''}`}
      aria-hidden={!isVisible}
      ref={containerRef}
    >
      {/* Effet de particules en arrière-plan */}
      <canvas 
        ref={particlesRef} 
        className="particles-canvas"
      />
      
      {/* Gradient qui suit la souris */}
      <div 
        className="mouse-gradient"
        style={{ background: gradientPosition }}
      />
      
      {/* Bouton de fermeture */}
      <div 
        className="travaux-close" 
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClose();
          }
        }}
        role="button"
        aria-label="Fermer la section Travaux"
        tabIndex={isVisible ? 0 : -1}
      >
        <span></span>
        <span></span>
      </div>
      
      <div className={`travaux-container ${isLoaded ? 'loaded' : ''}`}>
        <header className={`travaux-header ${animatedItems.includes('header') ? 'animated' : ''}`}>
          <h2 className="travaux-title">Mes Réalisations</h2>
          <p className="travaux-subtitle">
            <span>Découvrez mes différents projets</span>
          </p>
        </header>
        
        {/* Sphère de compétences 3D */}
        <div className={`skills-sphere-container ${animatedItems.includes('sphere') ? 'animated' : ''}`}>
          <motion.div
            className="skills-sphere"
            ref={skillsSphereRef}
            animate={{ rotateY: 360 }}
            transition={{ 
              duration: 20, 
              ease: "linear", 
              repeat: Infinity,
              repeatType: "loop"
            }}
          >
            {skills.map((skill, index) => {
              // Calculer une position 3D aléatoire sur une sphère
              const phi = Math.acos(-1 + (2 * index) / skills.length);
              const theta = Math.sqrt(skills.length * Math.PI) * phi;
              
              const x = 80 * Math.cos(theta) * Math.sin(phi);
              const y = 80 * Math.sin(theta) * Math.sin(phi);
              const z = 80 * Math.cos(phi);
              
              return (
                <motion.div
                  key={skill.name}
                  className="skill-tag"
                  style={{
                    transform: `translate(-50%, -50%) translate3d(${x}px, ${y}px, ${z}px)`,
                    backgroundColor: skill.color,
                    zIndex: Math.round(z) + 100
                  }}
                  whileHover={{ scale: 1.2, zIndex: 1000 }}
                >
                  {skill.name}
                </motion.div>
              );
            })}
          </motion.div>
        </div>
        
        {/* Métriques de projets */}
        <div className={`project-metrics ${animatedItems.includes('metrics') ? 'animated' : ''}`}>
          <h3 className="metrics-title">Mes compétences en chiffres</h3>
          
          <div className="metrics-grid">
            <div className="metric-card">
              <div className="metric-icon">
                <i className="fas fa-code-branch"></i>
              </div>
              <div className="metric-value">{projects.length}</div>
              <div className="metric-label">Projets réalisés</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">
                <i className="fas fa-layer-group"></i>
              </div>
              <div className="metric-value">
                {Object.keys(projects.reduce((acc, project) => {
                  acc[project.category] = true;
                  return acc;
                }, {})).length}
              </div>
              <div className="metric-label">Catégories</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">
                <i className="fas fa-laptop-code"></i>
              </div>
              <div className="metric-value">
                {Object.keys(projects.reduce((acc, project) => {
                  project.tags.forEach(tag => {
                    acc[tag] = true;
                  });
                  return acc;
                }, {})).length}
              </div>
              <div className="metric-label">Technologies</div>
            </div>
            
            <div className="metric-card">
              <div className="metric-icon">
                <i className="fas fa-calendar-check"></i>
              </div>
              <div className="metric-value">+2</div>
              <div className="metric-label">Années d'expérience</div>
            </div>
          </div>
          
          {/* Graphique des technologies les plus utilisées */}
          <div className="tech-graph">
            <h4 className="graph-title">Technologies les plus utilisées</h4>
            <div className="tech-bars">
              {Object.entries(projects.reduce((acc, project) => {
                project.tags.forEach(tag => {
                  acc[tag] = (acc[tag] || 0) + 1;
                });
                return acc;
              }, {}))
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([tag, count], index) => {
                  const tagInfo = techTags.find(t => t.id === tag) || { name: tag, icon: 'fas fa-code' };
                  const maxCount = Math.max(...Object.values(projects.reduce((acc, project) => {
                    project.tags.forEach(tag => {
                      acc[tag] = (acc[tag] || 0) + 1;
                    });
                    return acc;
                  }, {})));
                  
                  return (
                    <div key={tag} className="tech-bar-container">
                      <div className="tech-info">
                        <div className="tech-icon">
                          <i className={tagInfo.icon}></i>
                        </div>
                        <div className="tech-name">{tagInfo.name}</div>
                      </div>
                      <div className="tech-bar-wrapper">
                        <motion.div 
                          className="tech-bar"
                          initial={{ width: '0%' }}
                          animate={{ width: `${(count / maxCount) * 100}%` }}
                          transition={{ duration: 1, delay: 0.4 + (index * 0.1) }}
                          style={{ 
                            background: `linear-gradient(90deg, ${tagInfo.color || '#b387f6'}, ${tagInfo.color || '#5c7afa'}80)` 
                          }}
                        ></motion.div>
                      </div>
                      <div className="tech-count">{count}</div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
        
        {/* Contrôles de filtrage */}
        <div className={`travaux-controls ${animatedItems.includes('controls') ? 'animated' : ''}`}>
          <div className="search-filter-container">
            <div className="search-container">
              <input
                type="text"
                className="search-input"
                placeholder="Rechercher un projet..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Rechercher un projet"
              />
              <span className="search-icon">
                <i className="fas fa-search"></i>
              </span>
            </div>
            
            <div className="date-filter-container">
              <select
                className="date-filter"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                aria-label="Filtrer par période"
              >
                <option value="all">Toutes les périodes</option>
                <option value="last-month">Dernier mois</option>
                <option value="last-six-months">6 derniers mois</option>
                <option value="last-year">Dernière année</option>
                <option value="older">Plus d'un an</option>
              </select>
              <span className="date-filter-icon">
                <i className="fas fa-calendar-alt"></i>
              </span>
            </div>
          </div>
          
          <div className="filter-tags">
            {techTags.map((tag, index) => (
              <button
                key={tag.id}
                className={`filter-tag ${activeFilter === tag.id ? 'active' : ''}`}
                onClick={() => setActiveFilter(tag.id)}
                aria-pressed={activeFilter === tag.id}
                style={{ 
                  '--tag-color': tag.color,
                  animationDelay: `${index * 0.05}s`
                }}
              >
                <i className={tag.icon}></i>
                <span>{tag.name}</span>
              </button>
            ))}
          </div>
          
          <div className={`projects-count ${isFiltering ? 'filtering' : ''}`}>
            <span className="count-value">{filteredProjects.length}</span>
            <span className="count-label">projets trouvés</span>
            {activeFilter !== 'all' && (
              <div className="active-filter">
                <i className={techTags.find(t => t.id === activeFilter)?.icon}></i>
                <span>{techTags.find(t => t.id === activeFilter)?.name}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Grille de projets */}
        <div className={`projects-grid ${animatedItems.includes('projects') ? 'animated' : ''}`}>
          <AnimatePresence mode="wait">
            {filteredProjects.length > 0 ? (
              filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="project-card"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 30 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                >
                  {/* Badge pour projets spéciaux */}
                  {(project.isRecent || project.isFeatured) && (
                    <div className={`project-badge ${project.isRecent ? 'recent' : 'featured'}`}>
                      <span>{project.isRecent ? 'Nouveau' : 'Top Projet'}</span>
                      <i className={project.isRecent ? 'fas fa-star' : 'fas fa-award'}></i>
                    </div>
                  )}
                  
                  <div className="project-image">
                    <img src={project.image} alt={project.title} loading="lazy" />
                    <div className="project-overlay">
                      <div className="project-actions">
                        <button 
                          className="view-details-btn"
                          onClick={() => handleProjectClick(project)}
                          aria-label={`Voir les détails du projet ${project.title}`}
                        >
                          <i className="fas fa-eye"></i>
                          <span>Détails</span>
                        </button>
                        {project.link && (
                          <a 
                            href={project.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="view-live-btn"
                          >
                            <i className="fas fa-external-link-alt"></i>
                            <span>Visiter</span>
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="project-info">
                    <h3 className="project-title">{project.title}</h3>
                    
                    <div className="project-meta">
                      <span className="project-category">
                        <i className="fas fa-folder"></i>
                        {project.categoryName}
                      </span>
                      <span className="project-date">
                        <i className="fas fa-calendar"></i>
                        {formatDate(project.date)}
                      </span>
                    </div>
                    
                    <div className="project-tags">
                      {project.tags.slice(0, 3).map((tag) => {
                        const tagInfo = techTags.find(t => t.id === tag) || { name: tag, icon: 'fas fa-code' };
                        return (
                          <span key={tag} className="project-tag" style={{ '--tag-color': tagInfo.color }}>
                            <i className={tagInfo.icon}></i>
                            <span>{tagInfo.name}</span>
                          </span>
                        );
                      })}
                      {project.tags.length > 3 && (
                        <span className="project-tag-more">+{project.tags.length - 3}</span>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <motion.div 
                className="empty-state"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="empty-icon">
                  <i className="fas fa-search"></i>
                </div>
                <h3>Aucun projet trouvé</h3>
                <p>Essayez d'autres critères de recherche ou filtres</p>
                <button 
                  className="reset-filters-btn"
                  onClick={() => {
                    setActiveFilter('all');
                    setSearchQuery('');
                    setDateFilter('all');
                  }}
                >
                  <i className="fas fa-sync-alt"></i>
                  <span>Réinitialiser les filtres</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
      
      {/* Modal de détail du projet */}
      <AnimatePresence>
        {selectedProject && (
          <div className="project-modal">
            <div 
              className="project-modal-backdrop" 
              onClick={closeProjectDetails}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeProjectDetails();
              }}
              role="button"
              tabIndex={0}
              aria-label="Fermer les détails du projet"
            ></div>
            
            <motion.div 
              className="modal-content"
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
            >
              <button 
                className="modal-close" 
                onClick={closeProjectDetails}
                aria-label="Fermer les détails du projet"
              >
                <i className="fas fa-times"></i>
              </button>
              
              <div className="modal-grid">
                <div className="modal-image">
                  <img src={selectedProject.image} alt={selectedProject.title} loading="lazy" />
                </div>
                
                <div className="modal-details">
                  <div className="modal-header">
                    <span className="modal-category">{selectedProject.categoryName}</span>
                    <h2 className="modal-title">{selectedProject.title}</h2>
                  </div>
                  
                  <div className="project-timeline-tav">
                    <div className="timeline-tav-dot"></div>
                    <div className="timeline-tav-date">{formatDate(selectedProject.date)}</div>
                  </div>
                  
                  <div className="modal-tags">
                    {selectedProject.tags.map((tag) => {
                      const tagInfo = techTags.find(t => t.id === tag) || { name: tag, icon: 'fas fa-code' };
                      return (
                        <span key={tag} className="modal-tag" style={{ '--tag-color': tagInfo.color }}>
                          <i className={tagInfo.icon}></i>
                          <span>{tagInfo.name}</span>
                        </span>
                      );
                    })}
                  </div>
                  
                  <div className="modal-description">
                    <p>{selectedProject.description}</p>
                  </div>
                  
                  {selectedProject.link && (
                    <a 
                      href={selectedProject.link} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="visit-btn"
                    >
                      <span>Visiter le projet</span>
                      <i className="fas fa-external-link-alt"></i>
                    </a>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Travaux;